import { useMemo } from "react";

import { NavProfile, isAuthorityRole } from "constants/field-values";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import {
	NAV_PROFILES,
	NAV_PROFILE_FROM_DOMAIN,
	NavProfileConfig,
	toNavProfile
} from "utils/nav-profile";

const featureFlagOllieEnabled = process.env.REACT_APP_FF_IRIS_ENABLED == "true";

interface UseNavProfileResult {
	navProfile: NavProfileConfig;
	// False while the profile could still change when the user data arrives.
	isNavProfileResolved: boolean;
}

/**
 * Chooses the side navigation profile. The first source that has a value wins:
 * 1. The domain of the page (REACT_APP_NAV_PROFILE_DOMAINS).
 * 2. The nav_profile of the organization of the user.
 * 3. The role of the user (authority roles use the Ollie profile).
 * 4. The default profile.
 */
export const useNavProfile = (): UseNavProfileResult => {
	const { loggedUserValue } = useLoggedUser();
	const isUserLoaded = !!loggedUserValue?.id;
	const organizationNavProfile = loggedUserValue?.organization?.nav_profile;
	const userRole = loggedUserValue?.role;

	return useMemo(() => {
		const navProfileFromOrganization = toNavProfile(organizationNavProfile);
		const navProfileFromRole = isAuthorityRole(userRole) ? NavProfile.Ollie : null;

		let navProfileId =
			NAV_PROFILE_FROM_DOMAIN ?? navProfileFromOrganization ?? navProfileFromRole ?? NavProfile.Default;

		// The Ollie screens do not exist when their feature flag is off.
		if (navProfileId === NavProfile.Ollie && !featureFlagOllieEnabled) {
			navProfileId = NavProfile.Default;
		}

		return {
			navProfile: NAV_PROFILES[navProfileId],
			isNavProfileResolved: !!NAV_PROFILE_FROM_DOMAIN || isUserLoaded
		};
	}, [isUserLoaded, organizationNavProfile, userRole]);
};
